import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Copy, X } from "lucide-react";
import { useCallsApi } from "../../api/callsApi";

function formatDuration(totalSeconds) {
  const seconds = Math.max(Math.round(totalSeconds || 0), 0);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

// Linha "[mm:ss] Quem: texto" da transcrição gravada no backend.
function parseLine(line) {
  const match = line.match(/^\[(\d{2,}:\d{2})\] ([^:]+): (.*)$/);
  return match ? { time: match[1], who: match[2], text: match[3] } : { time: "", who: "", text: line };
}

const CallTranscriptModal = ({ callId, onClose }) => {
  const api = useCallsApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api
      .getTranscript(callId)
      .then((result) => !cancelled && setData(result))
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [api, callId]);

  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copy = async () => {
    const text = [data?.summary && `RESUMO\n${data.summary}`, `TRANSCRIÇÃO\n${data?.transcript || ""}`]
      .filter(Boolean)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Transcrição copiada");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl dark:bg-gray-800"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Transcrição da {data?.video ? "videochamada" : "ligação"}
            </h2>
            {data && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(data.startedAt).toLocaleString("pt-BR")} · duração{" "}
                {formatDuration(data.durationSeconds)} · transcrição automática, pode conter erros
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {data && (
              <button
                onClick={copy}
                title="Copiar resumo e transcrição"
                className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                <Copy className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onClose}
              title="Fechar"
              className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-4">
          {!data && !error && <p className="text-sm text-gray-500 dark:text-gray-400">Carregando…</p>}
          {error && <p className="text-sm text-red-500">{error}</p>}

          {data?.summary && (
            <div className="mb-4 rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
              <p className="mb-1 text-xs font-semibold uppercase text-green-700 dark:text-green-400">Resumo</p>
              <p className="whitespace-pre-line text-sm text-gray-900 dark:text-gray-100">{data.summary}</p>
            </div>
          )}

          {data?.transcript && (
            <div className="space-y-2">
              {data.transcript.split("\n").map((line, index) => {
                const { time, who, text } = parseLine(line);
                const isClient = who === "Cliente";
                return (
                  <div key={index} className="flex gap-2 text-sm">
                    <span className="w-12 shrink-0 tabular-nums text-xs leading-5 text-gray-400">{time}</span>
                    <p className="text-gray-900 dark:text-gray-100">
                      {who && (
                        <span
                          className={`font-semibold ${
                            isClient ? "text-blue-700 dark:text-blue-400" : "text-green-700 dark:text-green-400"
                          }`}
                        >
                          {who}:{" "}
                        </span>
                      )}
                      {text}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallTranscriptModal;
